import React, { useState, useEffect } from 'react';
import SimulatorPanel, { EMPTY_DEVICE, cloneDevice } from './components/SimulatorPanel';
import ResultsPanel from './components/ResultsPanel';
import AvarodhaPanel from './components/AvarodhaPanel';
import CurvaPanel from './components/CurvaPanel';
import ProfilesPanel from './components/ProfilesPanel';
import PLPanel from './components/PLPanel';
import EQEPanel from './components/EQEPanel';
import CVPanel from './components/CVPanel';
import PLAnalysisPanel from './components/PLAnalysisPanel';
import ParameterLibraryPanel from './components/ParameterLibraryPanel';
import PersistentAI from './components/PersistentAI';
import FeedbackPanel from './components/FeedbackPanel';
import LoginPanel from './components/LoginPanel';
import SetPasswordPanel from './components/SetPasswordPanel';
import Header from './components/Header';
import ComparisonModal from './components/ComparisonModal';
import { WorkspaceContextProvider } from './context/WorkspaceContext';
import { supabase, authEnabled } from './supabase';
import { getStoredTheme } from './theme';
import './App.css';

// Detect a Supabase invite / password-reset landing. The link carries the
// token in the URL hash (…#access_token=…&type=recovery|invite), which the
// Supabase client auto-exchanges for a session on load. Without this check
// the user would be silently logged in WITHOUT ever choosing a password.
// Captured at MODULE LOAD (before Supabase's async detectSessionInUrl can
// strip the hash) so the flag survives even after the token is consumed.
const INVITE_OR_RESET_LANDING = (() => {
  try {
    const hash = window.location.hash || '';
    const qs = window.location.search || '';
    const t = new URLSearchParams(hash.replace(/^#/, '')).get('type')
           || new URLSearchParams(qs).get('type');
    return t === 'recovery' || t === 'invite';
  } catch {
    return false;
  }
})();

// ─── Default simSettings ────────────────────────────────────────────────
// Stage 2: simSettings (T, V-sweep, illumination, solver) lifted from
// SimulatorPanel to App so the WorkspaceContext provider can see them.
const DEFAULT_SIM_SETTINGS = {
  temperature_K: 300,
  V_start: 0.0,
  V_end: 1.1,
  V_points: 30,
  illuminated: true,
  solver_preference: 'auto',
  // ── Solver physics (Phase 1B) — opt-in. All defaults mean NOTHING is
  // sent to the backend, so an untouched panel produces the exact
  // pre-Phase-1B payload. E_cnl_eV starts null so the first enable can
  // seed it from the absorber Eg (half-gap) when known.
  jv_mode: 'coupled',   // v1.1.0 default — voltage-dependent collection in the JV
  optics_model: 'beer_lambert',
  contact_bc: 'pinned',
  chi_transport: false,
  wg_series_resistance: false,
  front_interface_states: {
    enabled: false, Dit_cm2eV: 1e12, E_cnl_eV: null, sigma_n: 1e-15, sigma_p: 1e-15,
  },
  back_interface_states: {
    enabled: false, Dit_cm2eV: 1e12, E_cnl_eV: null, sigma_n: 1e-15, sigma_p: 1e-15,
  },
};

// ─── AI-spec → device converter ─────────────────────────────────────────
// Maps the chat-emitted device_spec JSON shape (per backend
// CHAT_V2_SYSTEM_PROMPT > ## Device Specification Output) onto the
// EMPTY_DEVICE shape that DeviceArchitectureModal and the solver expect.
// Unmapped sections (substrate, frontInterface, backInterface, contacts,
// illumination, traps) stay at EMPTY_DEVICE defaults — user can edit
// in the modal before running.
export const SPEC_ROLE_TO_SECTION = {
  TCO:          'frontContact',
  buffer:       'frontContact',
  absorber:     'absorber',
  back_contact: 'backContact',
};

export function specLayerToDeviceLayer(specLayer, section) {
  // Both `material` (canonical code) and `name` (display string) are
  // required on every layer. The simulator reads `material`; the modal
  // reads `name` for the summary string (`l.name.split(' ')[0]` at
  // DeviceArchitectureModal:577-579). Default `name` to `material` when
  // the spec doesn't carry a separate display name — matches the
  // pattern at DeviceArchitectureModal:431
  // (`name: MAT_DISPLAY[mat] || mat`).
  //
  // Numeric-field defaults: the backend Pydantic schema requires
  // numeric values, not null, but the AI device-spec emit format
  // legitimately omits fields per layer role (TCO layers don't have Na,
  // buffer layers don't have Nd, etc.). Applying user-specified
  // sentinel defaults so layers from the AI path don't 422 the
  // simulator endpoint. The CHAT_V2_SYSTEM_PROMPT also instructs the
  // model to emit Na_cm3:0 / Nd_cm3:0 explicitly for undoped layers,
  // but these defaults backstop the case where the model omits anyway.
  const mat = specLayer.material || '';
  // Track whether χ was explicitly emitted vs. supplied by the default below.
  // A defaulted χ on an absorber adjacent to a differing-Eg absorber is the
  // exact condition that manufactures a blocking interface — see the topology
  // guard in specToDevice. `_chiDefaulted` is transient metadata: serializeLayer
  // (SimulatorPanel) whitelists fields, so it never reaches the API payload.
  const chiExplicit = specLayer.chi_eV != null;
  return {
    id: Math.random().toString(36).slice(2),
    category: section,
    material: mat,
    name:     mat,
    thickness_um: specLayer.thickness_nm != null
      ? specLayer.thickness_nm / 1000
      : 0,
    Eg:       specLayer.Eg_eV       ?? 1.5,
    chi:      specLayer.chi_eV      ?? 4.0,
    _chiDefaulted: !chiExplicit,
    eps_r:    specLayer.eps_r       ?? 10,
    Na:       specLayer.Na_cm3      ?? 0,
    Nd:       specLayer.Nd_cm3      ?? 0,
    mu_n:     specLayer.mu_n_cm2Vs  ?? 100,
    mu_h:     specLayer.mu_h_cm2Vs  ?? 25,
    tau_n_ns: specLayer.tau_n_ns    ?? 10,
    tau_p_ns: specLayer.tau_p_ns    ?? 10,
    traps: [],
  };
}

export function specToDevice(spec) {
  const base = cloneDevice(EMPTY_DEVICE);
  if (!spec || !Array.isArray(spec.stack)) return base;
  // Reset the three layer sections we're filling; other sections retain
  // their EMPTY_DEVICE defaults.
  base.frontContact.layers = [];
  base.absorber.layers     = [];
  base.backContact.layers  = [];
  for (const layer of spec.stack) {
    const section = SPEC_ROLE_TO_SECTION[layer.role];
    if (!section) continue;   // unknown role → skip (no-op)
    base[section].layers.push(specLayerToDeviceLayer(layer, section));
  }
  // ── Topology guard ──────────────────────────────────────────────────────
  // Silent χ defaults must not manufacture a blocking interface. If the
  // absorber section has >1 layer with DIFFERING Eg AND their χ are equal (or
  // any χ was defaulted rather than explicitly emitted), the stack is a
  // stacked-abrupt graded absorber — the CdSeTe/CdTe rollover topology. We do
  // NOT auto-collapse to a single graded layer: removing the step correctly
  // needs the intended χ at each end (physical intent the converter lacks), so
  // synthesizing one would be inventing physics. Flag it instead, so it isn't
  // silently simulated as if the topology were intended.
  // Eg threshold 0.05 eV matches detectJunctionType (SimulatorPanel.js:46) —
  // one definition of "bands differ", and catch more rather than fewer.
  const abs = base.absorber.layers;
  if (abs.length > 1) {
    const egs = abs.map(l => l.Eg);
    const egDiffers = Math.max(...egs) - Math.min(...egs) > 0.05;
    const chis = abs.map(l => l.chi);
    const chiEqual = Math.max(...chis) - Math.min(...chis) <= 1e-6;
    const anyChiDefaulted = abs.some(l => l._chiDefaulted);
    if (egDiffers && (chiEqual || anyChiDefaulted)) {
      base.unvalidatedTopology = true;
      base.topologyWarning =
        `Unvalidated topology: ${abs.length} absorber layers with differing Eg but ` +
        `${chiEqual ? 'equal χ' : 'a defaulted χ'}. Equal/defaulted χ across an abrupt ` +
        `Eg step creates a blocking interface (rollover). A graded absorber should be ONE ` +
        `graded layer; a true heterojunction needs deliberate band offsets. Review before ` +
        `simulating.`;
      // eslint-disable-next-line no-console
      console.warn(`[specToDevice] ${base.topologyWarning}`, { absorber: abs });
    }
  }
  return base;
}

export default function App() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('simulation');
  const [session, setSession] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(!authEnabled);
  // True while the user must set a password (arrived via invite/reset link).
  const [recoveryMode, setRecoveryMode] = useState(INVITE_OR_RESET_LANDING);
  const [savedSims, setSavedSims] = useState([]);
  const [selectedSims, setSelectedSims] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [pendingDefects, setPendingDefects] = useState(null);
  // Theme lives at the top so every panel can receive it as a prop and
  // render reactively on toggle.  Header owns the toggle button and
  // reports back via onThemeChange.
  const [theme, setTheme] = useState(() => getStoredTheme());

  // Stage 2 lift — device + simSettings move here so the
  // WorkspaceContext can read them. SimulatorPanel and
  // DeviceArchitectureModal receive them via props.
  const [device, setDevice] = useState(() => cloneDevice(EMPTY_DEVICE));
  const [simSettings, setSimSettings] = useState(DEFAULT_SIM_SETTINGS);

  // ── AI device-spec load trigger ────────────────────────────────────────
  // Incrementing counter SimulatorPanel watches via useEffect to open
  // DeviceArchitectureModal pre-populated whenever the chat loads a new
  // AI-proposed device. Counter (not boolean) so successive loads always
  // fire — even if the modal was just closed.
  const [openModalTrigger, setOpenModalTrigger] = useState(0);

  // ── Parameter-library defect → device integration ─────────────────────
  // Inserts a bulk trap (internal row shape {id, type, Et, Et_ref,
  // sigma_n, sigma_p, Nt} — same as DeviceArchitectureModal.addTrap and
  // PLAnalysisPanel.applyQueue) into the PRIMARY absorber layer, defined
  // as the thickest layer in device.absorber.layers. Returns the target
  // layer's display name so the caller can confirm, or null when the
  // device has no absorber layer yet.
  const addDefectToAbsorber = (trap) => {
    const layers = device.absorber?.layers || [];
    if (layers.length === 0) return null;
    let idx = 0;
    layers.forEach((l, i) => {
      if ((l.thickness_um || 0) > (layers[idx].thickness_um || 0)) idx = i;
    });
    const target = {
      ...layers[idx],
      traps: [
        ...(layers[idx].traps || []),
        { ...trap, id: Math.random().toString(36).slice(2) },
      ],
    };
    const nextLayers = [...layers];
    nextLayers[idx] = target;
    setDevice({ ...device, absorber: { ...device.absorber, layers: nextLayers } });
    return target.name || target.material || 'absorber';
  };

  const handleDeviceSpecLoad = (spec) => {
    if (!spec) return;
    const next = specToDevice(spec);
    setDevice(next);
    // Minimum UI surface for the topology guard. `error` renders in
    // ResultsPanel (below). The string is also on `next.topologyWarning` for a
    // dedicated banner if one is built later.
    if (next.unvalidatedTopology) {
      setError(`⚠ ${next.topologyWarning}`);
    }
    setActiveTab('simulation');
    setOpenModalTrigger(t => t + 1);
  };

  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setSessionChecked(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((ev, s) => {
      setSession(s);
      // Supabase fires PASSWORD_RECOVERY when a reset/invite link is opened —
      // force the set-password step even if the URL hash was already consumed.
      if (ev === 'PASSWORD_RECOVERY') setRecoveryMode(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // When auth is configured, gate the app on a valid session.
  if (authEnabled) {
    if (!sessionChecked) return null;   // initial session check — blank frame
    // Invite/reset landing: the token auto-created a session, but the user
    // has not chosen a password yet — force it before granting access.
    if (recoveryMode) {
      // Waiting for the invite/reset token to finish exchanging into a
      // session — hold on a blank frame rather than flashing the login
      // screen (the auth listener will supply the session shortly).
      if (!session) return null;
      return (
        <SetPasswordPanel
          email={session?.user?.email}
          onDone={() => setRecoveryMode(false)}
        />
      );
    }
    if (!session) return <LoginPanel />;
  }

  return (
    <WorkspaceContextProvider
      activeTab={activeTab}
      device={device}
      simSettings={simSettings}
      latestSimulation={results}
      theme={theme}
    >
      <div className="app">
        <Header session={session} theme={theme} onThemeChange={setTheme} />
        <div className="tab-bar">
          <button
            className={`tab-btn ${activeTab === 'simulation' ? 'active' : ''}`}
            onClick={() => setActiveTab('simulation')}
          >
            Simulation
          </button>
          <button
            className={`tab-btn ${activeTab === 'profiles' ? 'active' : ''}`}
            onClick={() => setActiveTab('profiles')}
          >
            Profiles
            {results?.profiles && <span className="tab-badge">●</span>}
          </button>
          <button
            className={`tab-btn ${activeTab === 'curva' ? 'active' : ''}`}
            onClick={() => setActiveTab('curva')}
          >
            CurVA Analysis
            {results && <span className="tab-badge">●</span>}
          </button>
          <button
            className={`tab-btn ${activeTab === 'avarodha' ? 'active' : ''}`}
            onClick={() => setActiveTab('avarodha')}
          >
            Avarodha
            {results && <span className="tab-badge">●</span>}
          </button>
          <button
            className={`tab-btn ${activeTab === 'pl' ? 'active' : ''}`}
            onClick={() => setActiveTab('pl')}
          >
            PL / TRPL
          </button>
          <button
            className={`tab-btn ${activeTab === 'eqe' ? 'active' : ''}`}
            onClick={() => setActiveTab('eqe')}
          >
            EQE
          </button>
          <button
            className={`tab-btn ${activeTab === 'cv' ? 'active' : ''}`}
            onClick={() => setActiveTab('cv')}
          >
            C-V / Na(x)
          </button>
          <button
            className={`tab-btn ${activeTab === 'pl-analysis' ? 'active' : ''}`}
            onClick={() => setActiveTab('pl-analysis')}
          >
            PL Analysis
          </button>
          <button
            className={`tab-btn ${activeTab === 'parameter_library' ? 'active' : ''}`}
            onClick={() => setActiveTab('parameter_library')}
          >
            Parameter Library
          </button>
          <button
            className={`tab-btn ${activeTab === 'feedback' ? 'active' : ''}`}
            onClick={() => setActiveTab('feedback')}
          >
            Feedback
          </button>
        </div>

        <div className="app-body">
          <div className="workspace">
            {/* Keep both panels mounted — CSS hides on tab switch so state is preserved */}
            <main
              id="simulation-tab"
              className="main-layout with-rail"
              style={{ display: activeTab === 'simulation' ? undefined : 'none' }}
            >
              <SimulatorPanel
                setResults={setResults}
                setLoading={setLoading}
                setError={setError}
                loading={loading}
                theme={theme}
                results={results}
                savedSims={savedSims}
                setSavedSims={setSavedSims}
                selectedSims={selectedSims}
                setSelectedSims={setSelectedSims}
                setShowComparison={setShowComparison}
                pendingDefects={pendingDefects}
                clearPendingDefects={() => setPendingDefects(null)}
                device={device}
                setDevice={setDevice}
                simSettings={simSettings}
                setSimSettings={setSimSettings}
                openModalTrigger={openModalTrigger}
              />
              <ResultsPanel
                results={results}
                loading={loading}
                error={error}
                theme={theme}
              />
            </main>

            <main
              id="profiles-tab"
              className="main-layout"
              style={{ display: activeTab === 'profiles' ? undefined : 'none' }}
            >
              <ProfilesPanel results={results} theme={theme} />
            </main>

            <main
              id="curva-tab"
              className="main-layout"
              style={{ display: activeTab === 'curva' ? undefined : 'none' }}
            >
              <CurvaPanel simulationResults={results} theme={theme} />
            </main>

            <main
              id="avarodha-tab"
              className="main-layout"
              style={{ display: activeTab === 'avarodha' ? undefined : 'none' }}
            >
              <AvarodhaPanel results={results} theme={theme} />
            </main>

            <main
              id="pltrpl-tab"
              className="main-layout"
              style={{ display: activeTab === 'pl' ? undefined : 'none' }}
            >
              <PLPanel results={results} theme={theme} device={device} />
            </main>

            <main
              id="eqe-tab"
              className="main-layout"
              style={{ display: activeTab === 'eqe' ? undefined : 'none' }}
            >
              <EQEPanel results={results} theme={theme} device={device} simSettings={simSettings} />
            </main>

            <main
              id="cv-tab"
              className="main-layout"
              style={{ display: activeTab === 'cv' ? undefined : 'none' }}
            >
              <CVPanel results={results} theme={theme} />
            </main>

            <main
              id="pl-analysis-tab"
              className="main-layout"
              style={{ display: activeTab === 'pl-analysis' ? undefined : 'none' }}
            >
              <PLAnalysisPanel
                theme={theme}
                device={device}
                onApplyDefects={(defects) => {
                  setPendingDefects(defects);
                  setActiveTab('simulation');
                }}
              />
            </main>

            <main
              id="parameter-library-tab"
              className="main-layout"
              style={{ display: activeTab === 'parameter_library' ? undefined : 'none' }}
            >
              <ParameterLibraryPanel
                theme={theme}
                onAddDefectToAbsorber={addDefectToAbsorber}
              />
            </main>

            <main
              className="main-layout"
              style={{ display: activeTab === 'feedback' ? undefined : 'none' }}
            >
              <FeedbackPanel results={results} theme={theme} />
            </main>
          </div>

          <PersistentAI
            results={results}
            theme={theme}
            onDeviceSpecLoad={handleDeviceSpecLoad}
          />
        </div>

        {showComparison && (
          <ComparisonModal
            theme={theme}
            savedSims={savedSims}
            selectedSims={selectedSims}
            onClose={() => setShowComparison(false)}
          />
        )}
      </div>
    </WorkspaceContextProvider>
  );
}
